import type {
  AccountingFormat, AccountingKind, CommerceStatus, CommerceSummary, InvoiceDetail, InvoiceInput, InvoiceView, ManualPaymentInput, Page, PaymentLinkView,
  PaymentView, PaymentsSetupView, ProductInput, ProductView, QuoteInput, QuoteView, SendDocumentInput,
} from '@takeit4me/shared'
import type { Http } from '../http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
export type ProductBody = In<typeof ProductInput>
export type QuoteBody = In<typeof QuoteInput>
export type InvoiceBody = In<typeof InvoiceInput>
export type ManualPaymentBody = In<typeof ManualPaymentInput>
export type SendDocumentBody = In<typeof SendDocumentInput>
type Paging = { limit?: number; cursor?: string | null }
export interface AccountingExport { filename: string; rows: number; csv: string }

/** Console API methods: workstream E (products, quotes, invoices, payments, Stripe, accounting). */
export interface CommerceApi {
  products(q?: { active?: boolean; q?: string }): Promise<ProductView[]>
  createProduct(body: ProductBody): Promise<ProductView>
  updateProduct(id: string, body: Partial<ProductBody> & { active?: boolean }): Promise<ProductView>
  deleteProduct(id: string): Promise<{ ok: true }>

  quotes(q?: Paging & { status?: string; person_id?: string; organisation_id?: string }): Promise<Page<QuoteView>>
  getQuote(id: string): Promise<QuoteView>
  createQuote(body: QuoteBody): Promise<QuoteView>
  updateQuote(id: string, body: Partial<QuoteBody>): Promise<QuoteView>
  sendQuote(id: string, body?: SendDocumentBody): Promise<QuoteView>
  acceptQuote(id: string, body?: { po_number?: string | null }): Promise<{ quote: QuoteView; invoice: InvoiceDetail }>
  declineQuote(id: string, reason: string): Promise<QuoteView>

  invoices(q?: Paging & { status?: string; person_id?: string; organisation_id?: string; q?: string }): Promise<Page<InvoiceView>>
  getInvoice(id: string): Promise<InvoiceDetail>
  createInvoice(body: InvoiceBody): Promise<InvoiceDetail>
  updateInvoice(id: string, body: Partial<Omit<InvoiceBody, 'issue'>>): Promise<InvoiceDetail>
  issueInvoice(id: string): Promise<InvoiceDetail>
  voidInvoice(id: string, reason: string): Promise<InvoiceDetail>
  recordPayment(id: string, body: ManualPaymentBody): Promise<InvoiceDetail>
  markInvoicePaid(id: string, body?: Omit<ManualPaymentBody, 'amount_minor'>): Promise<InvoiceDetail>
  invoicePaymentLink(id: string): Promise<PaymentLinkView>
  sendInvoice(id: string, body?: SendDocumentBody & { include_link?: boolean }): Promise<InvoiceDetail>

  payments(q?: Paging & { person_id?: string; organisation_id?: string; invoice_id?: string }): Promise<Page<PaymentView>>
  commerceSummary(q: { person_id?: string; organisation_id?: string }): Promise<CommerceSummary>
  commerceStatus(): Promise<CommerceStatus>
  accountingExport(q: { format: AccountingFormat; kind: AccountingKind; from?: string; to?: string; account_code?: string }): Promise<AccountingExport>

  /** The company's own Stripe account (Connect): status, onboarding, re-check, stop using it. */
  paymentsSetup(): Promise<PaymentsSetupView>
  connectPayments(): Promise<{ url: string; setup: PaymentsSetupView }>
  refreshPayments(): Promise<PaymentsSetupView>
  disconnectPayments(reason: string): Promise<PaymentsSetupView>
}

const enc = encodeURIComponent
export const commerceMethods = (http: Http): CommerceApi => ({
  products: (q = {}) => http.get<ProductView[]>('/commerce/products', { active: q.active === undefined ? undefined : q.active ? 1 : 0, q: q.q }),
  createProduct: (b) => http.post<ProductView>('/commerce/products', b),
  updateProduct: (id, b) => http.patch<ProductView>(`/commerce/products/${enc(id)}`, b),
  deleteProduct: (id) => http.delete<{ ok: true }>(`/commerce/products/${enc(id)}`),

  quotes: (q = {}) => http.get<Page<QuoteView>>('/commerce/quotes', q),
  getQuote: (id) => http.get<QuoteView>(`/commerce/quotes/${enc(id)}`),
  createQuote: (b) => http.post<QuoteView>('/commerce/quotes', b),
  updateQuote: (id, b) => http.patch<QuoteView>(`/commerce/quotes/${enc(id)}`, b),
  sendQuote: (id, b = {}) => http.post<QuoteView>(`/commerce/quotes/${enc(id)}/send`, b),
  acceptQuote: (id, b = {}) => http.post<{ quote: QuoteView; invoice: InvoiceDetail }>(`/commerce/quotes/${enc(id)}/accept`, b),
  declineQuote: (id, reason) => http.post<QuoteView>(`/commerce/quotes/${enc(id)}/decline`, { reason }),

  invoices: (q = {}) => http.get<Page<InvoiceView>>('/commerce/invoices', q),
  getInvoice: (id) => http.get<InvoiceDetail>(`/commerce/invoices/${enc(id)}`),
  createInvoice: (b) => http.post<InvoiceDetail>('/commerce/invoices', b),
  updateInvoice: (id, b) => http.patch<InvoiceDetail>(`/commerce/invoices/${enc(id)}`, b),
  issueInvoice: (id) => http.post<InvoiceDetail>(`/commerce/invoices/${enc(id)}/issue`),
  voidInvoice: (id, reason) => http.post<InvoiceDetail>(`/commerce/invoices/${enc(id)}/void`, { reason }),
  recordPayment: (id, b) => http.post<InvoiceDetail>(`/commerce/invoices/${enc(id)}/payments`, b),
  markInvoicePaid: (id, b = {}) => http.post<InvoiceDetail>(`/commerce/invoices/${enc(id)}/mark-paid`, b),
  invoicePaymentLink: (id) => http.post<PaymentLinkView>(`/commerce/invoices/${enc(id)}/payment-link`),
  sendInvoice: (id, b = {}) => http.post<InvoiceDetail>(`/commerce/invoices/${enc(id)}/send`, b),

  payments: (q = {}) => http.get<Page<PaymentView>>('/commerce/payments', q),
  commerceSummary: (q) => http.get<CommerceSummary>('/commerce/summary', q),
  commerceStatus: () => http.get<CommerceStatus>('/commerce/status'),
  accountingExport: (q) => http.get<AccountingExport>('/commerce/export', q),

  paymentsSetup: () => http.get<PaymentsSetupView>('/commerce/payments-setup'),
  connectPayments: () => http.post<{ url: string; setup: PaymentsSetupView }>('/commerce/payments-setup/connect'),
  refreshPayments: () => http.post<PaymentsSetupView>('/commerce/payments-setup/refresh'),
  disconnectPayments: (reason) => http.post<PaymentsSetupView>('/commerce/payments-setup/disconnect', { reason }),
})
